"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef } from "react";
import { requestInvocaPNAPIRun } from "@/lib/utils/invoca/pnapi-run";
type InvocaSwapState = "pending" | "ready" | "failed";
interface UseInvocaNumberOptions {
  /**
   * If false, the hook is a no-op.
   * @default true
   */
  enabled?: boolean;
  /**
   * Server-rendered fallback number, used to detect a *real* swap.
   * Defaults to the node's initial text content.
   */
  fallback?: string;
  /**
   * Reveal the fallback and drop the loading treatment if PNAPI never swaps.
   * @default 2000
   */
  timeoutMs?: number;
  /**
   * Drive the `data-invoca-state` swap treatment (pending → ready/failed) that
   * CSS turns into the loading skeleton. Opt-in: only InvocaButton (whose
   * visible label is the swapped number) enables it. Plain tel: Buttons leave
   * it off — PNAPI still runs so the href can be swapped for tracking.
   * @default false
   */
  showSwapTreatment?: boolean;
}
interface UseInvocaNumberReturn {
  /** Ref callback — attach to the DOM element Invoca will mutate. */
  ref: React.RefCallback<HTMLElement>;
}
const onlyDigits = (value: string | null | undefined) => (value ?? "").replace(/\D/g, "");

/**
 * Ensures Invoca's PNAPI.run() follows the element's mount so that
 * phone-number swapping occurs (one coalesced run per settled DOM change,
 * shared with every other caller - lib/utils/invoca/pnapi-run.ts), and drives a swap-state treatment via the
 * `data-invoca-state` attribute ("pending" | "ready" | "failed"). Consumers
 * render `data-invoca-state="pending"` on the server so the skeleton shows
 * immediately, and the attribute flips to "ready"/"failed" once the swap lands
 * (or a safety timeout fires). The skeleton itself is pure CSS keyed off this
 * attribute — see `app/globals.css`.
 *
 * The hook intentionally avoids React state so re-renders never overwrite the
 * DOM values Invoca has set. The state attribute is written imperatively for
 * the same reason — React doesn't manage it, so it won't remove it on re-render.
 *
 * Because the Button/Link always renders the same text / href from props, the
 * virtual DOM is stable across re-renders and React will not touch the actual
 * DOM — allowing Invoca's mutations to persist.
 */
export function useInvocaNumber(t0) {
  const $ = _c(10);
  const {
    enabled: t1,
    fallback,
    timeoutMs: t2,
    showSwapTreatment: t3
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  const timeoutMs = t2 === undefined ? 2000 : t2;
  const showSwapTreatment = t3 === undefined ? false : t3;
  const observerRef = useRef(null);
  const timeoutRef = useRef(null);
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const cleanup = t4;
  let t5;
  if ($[1] !== enabled || $[2] !== fallback || $[3] !== showSwapTreatment || $[4] !== timeoutMs) {
    t5 = node => {
      cleanup();
      if (!node || !enabled) {
        return;
      }
      if (showSwapTreatment) {
        const setState = state => node.setAttribute("data-invoca-state", state);
        const fallbackDigits = onlyDigits(fallback ?? node.textContent);
        const isSwapped = () => {
          const digits = onlyDigits(node.textContent);
          return digits.length >= 10 && digits !== fallbackDigits;
        };
        if (isSwapped()) {
          setState("ready");
        } else {
          setState("pending");
          const observer = new MutationObserver(() => {
            if (isSwapped()) {
              setState("ready");
              cleanup();
            }
          });
          observer.observe(node, {
            childList: true,
            characterData: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["href"]
          });
          observerRef.current = observer;
          timeoutRef.current = setTimeout(() => {
            setState("failed");
            cleanup();
          }, timeoutMs);
        }
      }
      requestInvocaPNAPIRun();
    };
    $[1] = enabled;
    $[2] = fallback;
    $[3] = showSwapTreatment;
    $[4] = timeoutMs;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const ref = t5;
  let t6;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => cleanup;
    t7 = [cleanup];
    $[6] = t6;
    $[7] = t7;
  } else {
    t6 = $[6];
    t7 = $[7];
  }
  useEffect(t6, t7);
  let t8;
  if ($[8] !== ref) {
    t8 = {
      ref
    };
    $[8] = ref;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  return t8;
}